/**
 * Tâche 1484 (Eric) — Les fautes que les apprenants tapent le plus.
 *
 * > « Montre aux concepteurs les fautes que les apprenants tapent le plus,
 * >   pour enrichir le catalogue d'orthographe. »
 *
 * Source : les recherches réelles du quiz, du chatbot et de l'Explorateur.
 * Seuls les MOTS proches du vocabulaire du cabinet sont journalisés, jamais
 * les phrases envoyées au chatbot.
 *
 * Le bouton « Ajouter au catalogue » crée le terme cible s'il n'existe pas
 * encore : sans ça, le concepteur devrait passer par un autre panneau, créer
 * le terme, puis revenir coller la faute.
 */
import { useState } from "react";
import { Plus, SpinnerGap } from "@phosphor-icons/react";

const LIBELLES_SOURCE = {
  quiz: ["Quiz", "Quiz"],
  chat: ["Chatbot", "Chatbot"],
  explorateur: ["Explorateur", "Explorer"],
};

export default function FautesFrequentes({ donnees, chargement, lang, onAjouter }) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  const [cible, setCible] = useState({});
  const [enCours, setEnCours] = useState("");

  const fautes = donnees?.fautes || [];

  if (chargement) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500 py-10"
           data-testid="fautes-frequentes-chargement">
        <SpinnerGap size={16} className="animate-spin" />
        {tr("Chargement…", "Loading…")}
      </div>
    );
  }

  if (fautes.length === 0) {
    return (
      <p className="text-sm text-zinc-500 py-8" data-testid="fautes-frequentes-vide">
        {tr(
          "Aucune faute observée pour l'instant. Dès qu'un apprenant tape un mot mal orthographié dans le quiz, le chatbot ou l'Explorateur, il apparaît ici avec le nombre de fois où il a été tapé.",
          "No mistake observed yet. As soon as a learner types a misspelled word in the quiz, the chatbot or the Explorer, it shows up here with how many times it was typed."
        )}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="fautes-frequentes">
      <p className="text-xs text-zinc-600">
        {tr(
          `${donnees.total} faute(s) observée(s) dans les recherches réelles · ${donnees.a_traiter} pas encore au catalogue. Seuls les mots sont conservés, jamais les phrases envoyées au chatbot.`,
          `${donnees.total} mistake(s) observed in real searches · ${donnees.a_traiter} not yet in the catalogue. Only words are kept, never the sentences sent to the chatbot.`
        )}
      </p>
      <div className="border border-zinc-200 bg-white rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">{tr("Faute tapée", "Typed mistake")}</th>
              <th className="text-left px-4 py-2 font-semibold">{tr("Fois", "Times")}</th>
              <th className="text-left px-4 py-2 font-semibold">{tr("Personnes", "People")}</th>
              <th className="text-left px-4 py-2 font-semibold">{tr("Où", "Where")}</th>
              <th className="text-left px-4 py-2 font-semibold">{tr("Graphie correcte", "Correct spelling")}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {fautes.map((f) => {
              const valeur = cible[f.cle] !== undefined
                ? cible[f.cle]
                : (f.corrections[0] || "");
              return (
                <tr key={f.cle} data-testid={`faute-ligne-${f.cle}`}>
                  <td className="px-4 py-2 font-medium text-zinc-900">{f.mot_saisi}</td>
                  <td className="px-4 py-2 tabular-nums text-zinc-700" data-testid={`faute-occurrences-${f.cle}`}>
                    {f.occurrences}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-zinc-500">{f.personnes}</td>
                  <td className="px-4 py-2 text-[11px] text-zinc-500">
                    {f.sources.map((s) => (LIBELLES_SOURCE[s]
                      ? (lang === "en" ? LIBELLES_SOURCE[s][1] : LIBELLES_SOURCE[s][0])
                      : s)).join(", ")}
                  </td>
                  <td className="px-4 py-2">
                    {f.au_catalogue ? (
                      <span className="text-[11px] text-teal-700" data-testid={`faute-au-catalogue-${f.cle}`}>
                        {tr(`déjà au catalogue → ${f.terme_catalogue}`, `already in catalogue → ${f.terme_catalogue}`)}
                      </span>
                    ) : (
                      <input
                        value={valeur}
                        onChange={(e) => setCible({ ...cible, [f.cle]: e.target.value })}
                        className="w-44 px-2 py-1 border border-zinc-300 rounded text-sm"
                        placeholder={tr("graphie correcte", "correct spelling")}
                        data-testid={`faute-terme-${f.cle}`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!f.au_catalogue && (
                      <button
                        type="button"
                        disabled={!valeur.trim() || enCours === f.cle}
                        onClick={async () => {
                          setEnCours(f.cle);
                          await onAjouter(f.mot_saisi, valeur.trim());
                          setEnCours("");
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#002FA7] text-white text-xs font-semibold disabled:opacity-40"
                        data-testid={`faute-ajouter-${f.cle}`}
                      >
                        {enCours === f.cle
                          ? <SpinnerGap size={13} className="animate-spin" />
                          : <Plus size={13} weight="bold" />}
                        {tr("Ajouter au catalogue", "Add to catalogue")}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
